
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { useSEO, generateServiceSchema } from '../../utils/seo';

interface Transfer {
  destination: string;
  price: number;
}

const popularTransfers: Transfer[] = [
  { destination: "Val d'Isère", price: 390.95 },
  { destination: 'Val Thorens', price: 376.95 },
  { destination: 'Chamonix', price: 243.95 },
  { destination: 'Tignes', price: 390.95 },
  { destination: 'Courchevel', price: 348.95 },
  { destination: 'Genève', price: 82.95 },
  { destination: 'Morzine', price: 233.95 },
  { destination: 'Annecy City Centre', price: 152.95 },
  { destination: 'Méribel', price: 334.95 },
  { destination: 'Megève', price: 214.95 },
  { destination: 'Verbier', price: 438.95 },
  { destination: 'Avoriaz', price: 236.95 },
  { destination: 'Zermatt', price: 684.95 },
  { destination: 'La Plagne', price: 355.95 },
  { destination: 'Lausanne', price: 180.95 },
  { destination: 'Flaine', price: 229.95 },
  { destination: 'Chamonix Mont Blanc', price: 243.95 },
  { destination: 'Les Gets', price: 208.95 },
  { destination: 'Les Menuires', price: 355.95 },
  { destination: 'Les Deux Alpes', price: 453.95 },
];

const featuredDestinations = [
  {
    title: 'Ville de Genève',
    description: 'Arrivez à Genève en toute simplicité — transfert privé de l\'aéroport vers votre hôtel ou le centre-ville.',
    image: 'https://public.readdy.ai/ai/img_res/58a5ef2c4c11e890998a53e36582f9df.jpg'
  },
  {
    title: 'Geneva Airport Vers Chamonix',
    description: 'Découvrez plus',
    image: 'https://readdy.ai/api/search-image?query=chamonix%20mont%20blanc%20majestic%20snowy%20mountain%20peaks%20cable%20cars%20alpine%20village%20winter%20pristine%20white%20snow%20clear%20blue%20sky%20dramatic%20alpine%20scenery&width=800&height=600&seq=geneva-chamonix-002&orientation=landscape'
  },
  {
    title: 'Geneva Airport Navette',
    description: 'Découvrez plus',
    image: 'https://readdy.ai/api/search-image?query=luxury%20premium%20airport%20shuttle%20service%20modern%20elegant%20vehicle%20professional%20transport%20swiss%20alps%20mountain%20background%20high-end%20transfer%20service%20sophisticated%20clean%20design&width=800&height=600&seq=geneva-shuttle-002&orientation=landscape'
  },
  {
    title: 'Geneva Airport Vers Courchevel',
    description: 'Découvrez plus',
    image: 'https://readdy.ai/api/search-image?query=courchevel%20luxury%20exclusive%20ski%20resort%20elegant%20wooden%20chalets%20snowy%20alpine%20slopes%20upscale%20mountain%20village%20winter%20landscape%20premium%20destination%20clear%20blue%20sky%20pristine%20white%20snow&width=800&height=600&seq=geneva-courchevel-002&orientation=landscape'
  },
  {
    title: 'Geneva Airport Vers Val Thorens',
    description: 'Découvrez plus',
    image: 'https://readdy.ai/api/search-image?query=val%20thorens%20highest%20altitude%20ski%20resort%20dramatic%20snowy%20mountain%20peaks%20modern%20alpine%20architecture%20winter%20sports%20destination%20clear%20blue%20sky%20pristine%20powder%20snow%20spectacular%20mountain%20landscape&width=800&height=600&seq=geneva-valthorens-002&orientation=landscape'
  }
];

export default function GenevaAirportPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  const [tripType, setTripType] = useState<'round-trip' | 'one-way'>('round-trip');
  const [passengers, setPassengers] = useState(1);
  
  useSEO({
    title: 'Transfert Aéroport Genève vers Stations de Ski | Alpine Transfer Premium',
    description: 'Transfert privé depuis l\'aéroport de Genève (GVA) vers Chamonix, Courchevel, Val Thorens, Verbier et toutes les stations des Alpes. Service premium 24/7.',
    keywords: 'transfert Genève, navette Genève aéroport, VTC Genève Chamonix, transport Genève stations ski',
    canonical: '/geneva-airport',
    ogTitle: 'Transfert Aéroport Genève - Stations de Ski | Voyage Elite',
    ogDescription: 'Service de transfert privé premium depuis Genève vers les stations de ski des Alpes',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          '@id': `${siteUrl}/geneva-airport#webpage`,
          url: `${siteUrl}/geneva-airport`,
          name: 'Transferts depuis Genève Aéroport (GVA)',
          description: 'Service de transfert privé premium depuis l\'aéroport de Genève vers toutes les stations de ski',
          inLanguage: 'fr-FR',
        },
        generateServiceSchema(),
      ],
    },
  });

  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [featuredRef, featuredInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [transfersRef, transfersInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [experienceRef, experienceInView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [benefitsRef, benefitsInView] = useInView({ triggerOnce: true, threshold: 0.2 });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* HERO SECTION WITH BOOKING FORM */}
      <section ref={heroRef} className="relative min-h-[85vh] flex items-center justify-center overflow-hidden py-20">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=geneva%20airport%20modern%20terminal%20architecture%20swiss%20alps%20mountains%20background%20elegant%20clean%20professional%20aviation%20photography%20clear%20blue%20sky%20aircraft%20winter%20season%20bright%20natural%20lighting&width=1920&height=1080&seq=geneva-airport-hero-002&orientation=landscape')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60"></div>
        
        <div className="relative z-10 w-full max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            className="text-center mb-8"
          >
            <h1 className="font-playfair text-4xl lg:text-6xl font-bold text-white mb-4 leading-tight">
              Aéroport de Genève,<br />
              <span className="text-alpine-gold">Connexions Fluides</span>
            </h1>
            <p className="text-slate-200 font-inter text-lg lg:text-xl max-w-2xl mx-auto">
              Votre connexion aéroportuaire de confiance — transferts privés avec la précision suisse.
            </p>
          </motion.div>

          {/* BOOKING FORM */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-3xl shadow-2xl p-8 lg:p-10 max-w-4xl mx-auto"
          >
            {/* Trip Type Toggle */}
            <div className="flex gap-4 mb-8">
              <button
                onClick={() => setTripType('round-trip')}
                className={`flex-1 px-6 py-3 rounded-xl font-inter font-medium transition-all whitespace-nowrap cursor-pointer ${
                  tripType === 'round-trip'
                    ? 'bg-alpine-gold text-night-blue'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Aller-retour
              </button>
              <button
                onClick={() => setTripType('one-way')}
                className={`flex-1 px-6 py-3 rounded-xl font-inter font-medium transition-all whitespace-nowrap cursor-pointer ${
                  tripType === 'one-way'
                    ? 'bg-alpine-gold text-night-blue'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Aller simple
              </button>
            </div>

            {/* Form Fields */}
            <div className="space-y-6">
              {/* From Location */}
              <div>
                <label className="block text-slate-700 font-inter font-medium mb-2 text-sm">De</label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center">
                    <i className="ri-map-pin-line text-alpine-gold text-xl"></i>
                  </div>
                  <input
                    type="text"
                    value="Genève Aéroport (GVA)"
                    readOnly
                    className="w-full pl-14 pr-4 py-4 border-2 border-slate-200 rounded-xl font-inter text-sm text-night-blue bg-slate-50 cursor-not-allowed"
                  />
                </div>
              </div>

              {/* Spacer Icon */}
              <div className="flex justify-center">
                <div className="w-10 h-10 rounded-full bg-alpine-gold/10 flex items-center justify-center">
                  <i className="ri-arrow-down-line text-alpine-gold text-xl"></i>
                </div>
              </div>

              {/* To Location */}
              <div>
                <label className="block text-slate-700 font-inter font-medium mb-2 text-sm">Vers</label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center">
                    <i className="ri-map-pin-line text-alpine-gold text-xl"></i>
                  </div>
                  <input
                    type="text"
                    placeholder="Sélectionnez votre destination"
                    className="w-full pl-14 pr-4 py-4 border-2 border-slate-200 rounded-xl font-inter text-sm text-night-blue focus:border-alpine-gold focus:outline-none transition-colors cursor-pointer"
                  />
                </div>
              </div>

              {/* Dates */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-slate-700 font-inter font-medium mb-2 text-sm">Départ</label>
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center">
                      <i className="ri-calendar-line text-alpine-gold text-xl"></i>
                    </div>
                    <input
                      type="date"
                      className="w-full pl-14 pr-4 py-4 border-2 border-slate-200 rounded-xl font-inter text-sm text-night-blue focus:border-alpine-gold focus:outline-none transition-colors cursor-pointer"
                    />
                  </div>
                </div>
                {tripType === 'round-trip' && (
                  <div>
                    <label className="block text-slate-700 font-inter font-medium mb-2 text-sm">Retour</label>
                    <div className="relative">
                      <div className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center">
                        <i className="ri-calendar-line text-alpine-gold text-xl"></i>
                      </div>
                      <input
                        type="date"
                        className="w-full pl-14 pr-4 py-4 border-2 border-slate-200 rounded-xl font-inter text-sm text-night-blue focus:border-alpine-gold focus:outline-none transition-colors cursor-pointer"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Passengers */}
              <div>
                <label className="block text-slate-700 font-inter font-medium mb-2 text-sm">Passagers</label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center">
                    <i className="ri-user-line text-alpine-gold text-xl"></i>
                  </div>
                  <select
                    value={passengers}
                    onChange={(e) => setPassengers(Number(e.target.value))}
                    className="w-full pl-14 pr-4 py-4 border-2 border-slate-200 rounded-xl font-inter text-sm text-night-blue focus:border-alpine-gold focus:outline-none transition-colors cursor-pointer appearance-none"
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                      <option key={num} value={num}>
                        {num} {num === 1 ? 'passager' : 'passagers'}
                      </option>
                    ))}
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 text-xl pointer-events-none"></i>
                </div>
              </div>

              {/* Submit Button */}
              <Link
                to="/reservation"
                className="w-full inline-flex items-center justify-center gap-3 px-8 py-5 bg-alpine-gold text-night-blue rounded-xl font-inter font-bold text-lg hover:bg-night-blue hover:text-white transition-all shadow-lg whitespace-nowrap cursor-pointer"
              >
                <i className="ri-search-line text-xl"></i>
                Voir les tarifs
              </Link>
            </div>
          </motion.div>

          {/* Trust Badges */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.4 }}
            className="mt-10 flex flex-wrap justify-center gap-8 text-white"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-alpine-gold/20 flex items-center justify-center">
                <i className="ri-shield-check-line text-alpine-gold text-2xl"></i>
              </div>
              <div>
                <p className="font-inter font-bold text-sm">Des millions de personnes</p>
                <p className="font-inter text-xs text-slate-300">nous font confiance</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-alpine-gold/20 flex items-center justify-center">
                <i className="ri-customer-service-2-line text-alpine-gold text-2xl"></i>
              </div>
              <div>
                <p className="font-inter font-bold text-sm">Service d'assistance</p>
                <p className="font-inter text-xs text-slate-300">24h/24 et 7j/7</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-alpine-gold/20 flex items-center justify-center">
                <i className="ri-lock-line text-alpine-gold text-2xl"></i>
              </div>
              <div>
                <p className="font-inter font-bold text-sm">Personnalisé</p>
                <p className="font-inter text-xs text-slate-300">et sécurisé</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FEATURED DESTINATIONS SECTION */}
      <section ref={featuredRef} className="py-24 px-6 lg:px-12 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredDestinations.map((destination, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={featuredInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.1 * index }}
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all cursor-pointer"
              >
                <div className="h-64 w-full overflow-hidden">
                  <img
                    src={destination.image}
                    alt={destination.title}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-inter text-night-blue font-bold text-xl mb-3 group-hover:text-alpine-gold transition-colors">
                    {destination.title}
                  </h3>
                  <p className="text-slate-600 font-inter text-sm mb-4">
                    {destination.description}
                  </p>
                  <Link
                    to="/reservation"
                    className="inline-flex items-center gap-2 text-alpine-gold font-inter font-medium hover:gap-3 transition-all"
                  >
                    Découvrez plus
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* POPULAR TRANSFERS SECTION */}
      <section ref={transfersRef} className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={transfersInView ? { opacity: 1, y: 0 } : {}}
            className="text-center mb-16"
          >
            <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-night-blue mb-4">
              Transferts privés les plus réservés<br />
              <span className="text-alpine-gold">depuis l'aéroport de Genève</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {popularTransfers.map((transfer, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={transfersInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.03 * index }}
                className="group bg-white border-2 border-slate-200 rounded-2xl p-6 hover:border-alpine-gold hover:shadow-xl transition-all cursor-pointer"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-slate-500 font-inter text-xs mb-2">
                      <i className="ri-map-pin-line"></i>
                      <span>Genève Aéroport</span>
                    </div>
                    <h3 className="font-inter text-night-blue font-bold text-lg mb-1 group-hover:text-alpine-gold transition-colors">
                      {transfer.destination}
                    </h3>
                  </div>
                  <div className="w-10 h-10 rounded-xl bg-alpine-gold/10 flex items-center justify-center flex-shrink-0 group-hover:bg-alpine-gold/20 transition-colors">
                    <i className="ri-skiing-line text-alpine-gold text-xl"></i>
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-200">
                  <p className="text-slate-500 font-inter text-xs mb-2">Prix à partir de</p>
                  <p className="font-playfair text-alpine-gold font-bold text-2xl mb-4">
                    EUR {transfer.price.toFixed(2)}
                  </p>
                  <Link
                    to="/reservation"
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-night-blue text-white rounded-xl font-inter font-medium text-sm hover:bg-alpine-gold hover:text-night-blue transition-all whitespace-nowrap cursor-pointer"
                  >
                    Réserver
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* AIRPORT INFO SECTION */}
      <section className="py-24 px-6 lg:px-12 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-night-blue mb-6">
              Geneva Aéroport
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-3xl p-10 lg:p-12 shadow-xl border border-slate-200"
          >
            <p className="text-slate-700 font-inter text-base leading-relaxed">
              Commencez votre voyage comme il se doit — avec un transfert aéroport fluide, fiable et professionnel. Voyage Elite assure une expérience porte-à-porte sans accroc depuis le Geneva Airport jusqu'à votre destination, avec des prix fixes, des chauffeurs professionnels et une assistance 24h/24 et 7j/7. Chaque trajet comprend le suivi des vols, un temps d'attente flexible et un accueil personnalisé (meet & greet) aux arrivées. Oubliez le stress de chercher un transport — votre chauffeur sera déjà sur place, prêt à vous aider avec les bagages et à vous guider jusqu'au véhicule. Avec Voyage Elite, comptez sur la sécurité, le confort et la ponctualité — à chaque voyage depuis le Geneva Airport.
            </p>
          </motion.div>
        </div>
      </section>

      {/* WORLDWIDE SECTION */}
      <section className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-alpine-gold/10 rounded-full mb-8">
              <i className="ri-global-line text-alpine-gold text-2xl"></i>
              <span className="font-inter font-bold text-night-blue">Présent dans le monde entier</span>
            </div>
            
            <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-night-blue mb-6">
              Découvrez tous les aéroports<br />
              <span className="text-alpine-gold">à disposition de Voyage Elite</span>
            </h2>
            
            <Link
              to="/all-transfers"
              className="inline-flex items-center gap-3 px-10 py-5 bg-night-blue text-white rounded-full font-inter font-bold text-lg hover:bg-alpine-gold hover:text-night-blue transition-all shadow-xl whitespace-nowrap cursor-pointer mt-8"
            >
              Découvrez plus
              <i className="ri-arrow-right-line text-xl"></i>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* EXPERIENCE SECTION */}
      <section ref={experienceRef} className="py-24 px-6 lg:px-12 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={experienceInView ? { opacity: 1, y: 0 } : {}}
            className="text-center mb-16"
          >
            <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-night-blue mb-4">
              Guidé par l'expérience
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={experienceInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-3xl p-10 lg:p-12 shadow-xl border border-slate-200"
          >
            <p className="text-slate-700 font-inter text-base leading-relaxed mb-6">
              Depuis 2008, Voyage Elite propose des transferts aéroport basés sur la confiance, la précision et la constance.
            </p>
            <p className="text-slate-700 font-inter text-base leading-relaxed mb-6">
              Avec plus de 17 ans d'expérience, nous combinons technologie avancée et expertise locale pour garantir un trajet fluide — de l'atterrissage à l'arrivée.
            </p>
            <p className="text-alpine-gold font-inter text-xl font-bold">
              Un service fiable, éprouvé au fil de millions de trajets.
            </p>
          </motion.div>
        </div>
      </section>

      {/* BENEFITS SECTION */}
      <section ref={benefitsRef} className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={benefitsInView ? { opacity: 1, y: 0 } : {}}
            className="text-center mb-16"
          >
            <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-night-blue mb-4">
              Transferts privés depuis l'aéroport de Genève<br />
              <span className="text-alpine-gold">Simplifié.</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: 'ri-price-tag-line',
                title: 'Tarifs transparents et tout compris',
                description: 'Tarifs transparents et tout compris'
              },
              {
                icon: 'ri-customer-service-2-line',
                title: 'Support 24/7 & Réservation instantanée',
                description: 'Support 24/7 & Réservation instantanée'
              },
              {
                icon: 'ri-time-line',
                title: 'Attente gratuite de 60 minutes à l\'aéroport',
                description: 'Attente gratuite de 60 minutes à l\'aéroport'
              },
              {
                icon: 'ri-car-line',
                title: 'Flotte moderne pour chaque taille de groupe',
                description: 'Flotte moderne pour chaque taille de groupe'
              }
            ].map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={benefitsInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: 0.1 * index }}
                className="bg-gradient-to-br from-slate-50 to-white border-2 border-slate-200 rounded-2xl p-8 text-center hover:border-alpine-gold hover:shadow-xl transition-all"
              >
                <div className="w-16 h-16 bg-alpine-gold/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <i className={`${benefit.icon} text-alpine-gold text-3xl`}></i>
                </div>
                <h3 className="font-inter text-night-blue font-bold text-base mb-3">
                  {benefit.title}
                </h3>
                <p className="text-slate-600 font-inter text-sm leading-relaxed">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-24 px-6 lg:px-12 bg-gradient-to-br from-night-blue via-slate-900 to-night-blue">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-white mb-6">
              Réservez votre transfert depuis<br />
              <span className="text-alpine-gold">Genève Aéroport</span>
            </h2>
            <p className="text-slate-300 font-inter text-xl mb-10">
              Confirmation immédiate • Assistance 24/7 • Annulation simple
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
              <Link
                to="/reservation"
                className="inline-flex items-center gap-3 px-12 py-5 bg-alpine-gold text-night-blue rounded-full font-inter font-bold text-xl hover:bg-white transition-all shadow-2xl whitespace-nowrap cursor-pointer"
              >
                <i className="ri-calendar-check-line text-2xl"></i>
                Réserver en ligne
              </Link>
              <a
                href="tel:+33605606006"
                className="inline-flex items-center gap-3 px-12 py-5 bg-white/10 backdrop-blur-sm text-white border-2 border-white rounded-full font-inter font-bold text-xl hover:bg-white hover:text-night-blue transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line text-2xl"></i>
                +33 6 05 60 60 06
              </a>
            </div>

            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center text-slate-300">
              <div className="flex items-center gap-2">
                <i className="ri-whatsapp-line text-green-400 text-2xl"></i>
                <a href="https://wa.me/33605606006" target="_blank" rel="noopener noreferrer" className="font-inter hover:text-alpine-gold transition-colors cursor-pointer">
                  WhatsApp disponible
                </a>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-mail-line text-alpine-gold text-2xl"></i>
                <a href="mailto:contac.driver@outlook.fr" className="font-inter hover:text-alpine-gold transition-colors cursor-pointer">
                  contac.driver@outlook.fr
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
